import React, { useState, useEffect } from "react";
import { Table, Button, Breadcrumb, Card, message, Modal, Typography, Space, Tag } from "antd";
import { FolderOpenOutlined, EyeOutlined, AppstoreOutlined, FileTextOutlined } from "@ant-design/icons";
import { baseurl } from "../../../components/lib/settings";
import DocumentReader from "../../../components/documentReader/DocumentReader";
import { render } from "@testing-library/react";

const { Title, Text } = Typography;

const CompanyEdicoes = () => {
  const [editions, setEditions] = useState([]);
  const [selectedEdition, setSelectedEdition] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isReaderVisible, setIsReaderVisible] = useState(false);

  useEffect(() => {
    fetchActiveEditions();
  }, []);

  const fetchActiveEditions = async () => {
    setLoading(true);
    try {
      const response = await baseurl.get("v3/get_available_editions/by_company/");
      setEditions(response.data);
    } catch (error) {
      message.error("Erro ao carregar edições.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (editionId) => {
    setLoading(true);
    try {
      const response = await baseurl.get(`v3/edicoes/${editionId}/documents/`);
      setDocuments(response.data);
    } catch (error) {
      message.error("Erro ao carregar documentos.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditionClick = (record) => {
    setSelectedEdition(record);
    fetchDocuments(record.id);
  };

  const handleViewDocument = (record) => {
    setSelectedDocument(record);
    setIsReaderVisible(true);
  };

  const columnsEditions = [
    {
      title: "#",
      dataIndex: "id",
      key: "index",
      width: 80,
      render: (_, ___, index) => index +1,
    },
    {
      title: "Edição",
      dataIndex: "description",
      key: "description",
      render: (text) => <Text strong style={{ color: "#374151" }}>{text}</Text>,
    },
    {
      title: "Estado",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive) => (
        <Tag color={isActive ? "success" : "default"} style={{ borderRadius: "12px", padding: "0 12px" }}>
          {isActive ? "ATIVA" : "INATIVA"}
        </Tag>
      ),
    },
    {
      title: "Ações",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Button type="primary" shape="round" icon={<FolderOpenOutlined />} onClick={() => handleEditionClick(record)}>
          Ver Documentos
        </Button>
      ),
    },
  ];

  const columnsDocuments = [
    {
      title: "Descrição do Documento",
      dataIndex: "description",
      key: "description",
      render: (text) => (
        <Space>
          <FileTextOutlined style={{ color: "#1890ff" }} />
          <Text strong style={{ color: "#4b5563" }}>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Data de Criação",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => <Text type="secondary">{new Date(text).toLocaleString()}</Text>,
    },
    {
      title: "Ações",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Button type="default" shape="round" icon={<EyeOutlined />} onClick={() => handleViewDocument(record)}>
          Ler Documento
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px", background: "#f3f4f6", minHeight: "100vh" }}>
      <div style={{ marginBottom: "32px" }}>
        <Space direction="vertical" size={4}>
          <Title level={2} style={{ margin: 0, color: "#111827", fontWeight: 700 }}>
            Galeria de Edições
          </Title>
          <Text type="secondary" style={{ fontSize: "15px" }}>
            Navegue pelos documentos e regulamentos partilhados em cada edição.
          </Text>
        </Space>
      </div>

      <Breadcrumb style={{ marginBottom: "24px", fontSize: "14px", padding: "12px 24px", background: "#fff", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <Breadcrumb.Item onClick={() => { setSelectedEdition(null); setDocuments([]); }} style={{ cursor: 'pointer', color: selectedEdition ? '#1890ff' : '#374151', fontWeight: 500 }}>
          <AppstoreOutlined style={{ marginRight: 6 }} /> Edições Ativas
        </Breadcrumb.Item>
        {selectedEdition && (
          <Breadcrumb.Item style={{ color: "#111827", fontWeight: 600 }}>
            Documentos: {selectedEdition.description}
          </Breadcrumb.Item>
        )}
      </Breadcrumb>

      <Card 
        bordered={false} 
        style={{ borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}
        title={
          selectedEdition ? (
            <span style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937" }}>Documentos disponíveis para a edição {selectedEdition.description}</span>
          ) : (
            <span style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937" }}>Minhas Edições</span>
          )
        }
      >
        {!selectedEdition ? (
          <Table
            dataSource={editions}
            columns={columnsEditions}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10, position: ["bottomCenter"] }}
            rowClassName={() => "hoverable-row"}
          />
        ) : (
          <Table
            dataSource={documents}
            columns={columnsDocuments}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10, position: ["bottomCenter"] }}
            rowClassName={() => "hoverable-row"}
          />
        )}
      </Card>

      <Modal
        title={
          <Space>
            <EyeOutlined style={{ color: "#1890ff" }} />
            <span style={{ fontWeight: 600 }}>Visualização de Documento - {selectedDocument?.description}</span>
          </Space>
        }
        open={isReaderVisible}
        onCancel={() => { setIsReaderVisible(false); setSelectedDocument(null); }}
        footer={[
          <Button key="close" shape="round" type="primary" size="large" onClick={() => { setIsReaderVisible(false); setSelectedDocument(null); }}>
            Fechar Leitor
          </Button>
        ]}
        width={900}
        centered
        bodyStyle={{ height: '75vh', overflow: 'hidden', padding: 0, background: '#e5e7eb' }}
      >
        {selectedDocument && (
          <DocumentReader 
            fileBase64={selectedDocument.file_base64} 
            fileType={selectedDocument.file_type} 
          />
        )}
      </Modal>

      {/* Global Style for hoverable rows to add micro-interactions */}
      <style>{`
        .hoverable-row { transition: all 0.2s ease; cursor: pointer; }
        .hoverable-row:hover { background-color: #f8fafc !important; transform: scale(1.002); }
      `}</style>
    </div>
  );
};

export default CompanyEdicoes;
